"use client";
import { AnimatedGradientBackground, GradientPresets } from "@/components/Animation/GradientBackground";
import VantaWavesBackground from "@/components/Background/VantaWaves";
import IconBox from "@/components/Icons/iconbox";
import Link from "next/link";
import Image from "next/image";
import {
  ArrowRight,
  Brain,
  Layers,
  Users,
  Cloud,
  BarChart3,
  Zap,
  Building2,
  Pill,
  Landmark,
  Lightbulb,
  Globe2,
  Shield,
} from "lucide-react";

interface Specialism {
  title: string;
  description: string;
  icon: any;
  href: string;
}

interface Industry {
  name: string;
  icon: any;
  href: string;
}

interface Impact {
  client: string;
  description: string;
  href: string;
}

interface DeliveryCard {
  icon: any;
  title: string;
  text: string;
}

interface Focus {
  icon: any;
  title: string;
  text: string;
}

const focus: Focus = {
  icon: Lightbulb,
  title: "Our Focus",
  text: "We combine human insight with digital precision..."
};

const specialisms: Specialism[] = [
  {
    title: "Customer & Commercial Transformation",
    description:
      "Redefining how organisations create and capture value through data-led innovation and intelligent automation.",
    icon: Users,
    href: "/specialisms#customer-commercial",
  },
  {
    title: "Technology & Platforms",
    description:
      "Building secure, scalable digital foundations that enable agility, integration, and resilience.",
    icon: Cloud,
    href: "/specialisms#technology-platforms",
  },
  {
    title: "Data & AI",
    description:
      "Turning fragmented information into strategic intelligence enabling faster, evidence-based decisions.",
    icon: Brain,
    href: "/specialisms#data-ai",
  },
  {
    title: "People & Change",
    description:
      "Supporting lasting improvement by aligning leadership, culture, and skills with the changing nature of work.",
    icon: Globe2,
    href: "/specialisms#people-change",
  },
];

const industries: Industry[] = [
  { name: "Public Sector & Government", icon: Landmark, href: "/sectors#public-sector" },
  { name: "Pharma & Life Sciences", icon: Pill, href: "/sectors#pharma-life-sciences" },
  { name: "Energy & Infrastructure", icon: Zap, href: "/sectors#energy-infrastructure" },
  { name: "Financial Services", icon: Building2, href: "/sectors#financial-services" },
  { name: "Technology & Consumer", icon: Cloud, href: "/sectors#technology-consumer" },
];

const impacts: Impact[] = [
  {
    client: "Lloyds Banking Group",
    description:
      "Migrated £40bn in pension portfolios onto FNZ's cloud platform, improving data integrity and enabling AI-powered wealth management.",
    href: "/impact#financial-services",
  },
  {
    client: "UBS",
    description:
      "Led cross-functional delivery to harmonise Credit Suisse NFR systems with UBS, accelerating AI integration through scalable data unification.",
    href: "/impact#financial-services",
  },
  {
    client: "UK Government Agency",
    description:
      "Designed a sustainable data operating model improving transparency and accessibility by 40%.",
    href: "/impact#public-sector",
  },
];

const deliveryCards: DeliveryCard[] = [
  {
    icon: Layers,
    title: "Integrated Delivery",
    text: "We unite consulting, engineering, and change. One model from strategy to execution.",
  },
  {
    icon: Zap,
    title: "Agile Discipline",
    text: "Sprint-based programmes powered by automation pipelines ensure quality and pace.",
  },
  {
    icon: BarChart3,
    title: "Transparent Results",
    text: "We measure outcomes in real time with live KPIs that sustain accountability.",
  },
];

export default function HomePage() {
  return (
    <main className="min-h-screen z-40 text-text-primary">
      <AnimatedGradientBackground {...GradientPresets.homepage} />

      {/* HERO */}
      <section className="relative max-w-6xl mx-auto pt-40 px-6 text-left border border-standard overflow-hidden">
        <div className="absolute inset-0 z-0">
          <VantaWavesBackground/>
          {/* Multi-layer Overlay for better effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-200/15 via-blue-300/15 to-green-200/15" />
        </div>
        <h1 className="h1 mb-6 relative z-10">
          Turning potential into <br />
          <span className="text-text-secondary">intelligent impact.</span>
        </h1>
        <p className="body-lg max-w-3xl mb-10 relative z-10">
          We help organisations turn intelligent ambition into measurable outcomes by combining
          insight, technology, and human capability to deliver transformation that lasts.
        </p>
      </section>

      {/* WHO WE ARE / FOCUS */}
      <section className="max-w-6xl mx-auto mt-16 sm:mt-20 px-6">
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 card-glass items-start">
          {/* Who We Are Card */}
          <div>
            <h2 className="h2 mb-4">Who We Are</h2>
            <p className="body-md mb-4 sm:mb-6">
              Luminar Consult is a UK-based consultancy thriving at the intersection of business
              ambition, technology, and change. We bring structure to complexity, clarity to data,
              and confidence to transformation.
            </p>
            <p className="body-md">
              Every engagement is designed to turn intelligent ambition into measurable impact
              through data, AI, and digital systems that work together.
            </p>
          </div>

          {/* Our Focus Card */}
          <div className="card-glass flex flex-col items-start mt-6 sm:mt-10 gap-4 sm:gap-6">
            <div className="flex items-start gap-4">
              <IconBox icon={focus.icon} index={0} variant="badge" size="md" />
              <div>
                <h3 className="h3 mb-2">Our Focus</h3>
              </div>
            </div>
            <p className="body-lg">
              We combine human insight with digital precision. Our work connects strategy to
              systems, ensuring ideas thrive in practice.
            </p>
          </div>
        </div>
      </section>

      {/* HOW WE DELIVER */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24 card-glass text-center">
        <h2 className="h2 mb-6">How We Deliver</h2>
        <div className="grid backdrop-blur-sm sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
          {deliveryCards.map((card, index) => (
            <div key={index} className="relative flex flex-col items-center text-center sm:text-left">
              <IconBox icon={card.icon} index={0} variant="badge" size="md" className="mb-2" />
              <p className="body-lg mb-3 sm:mb-4 font-semibold text-text-primary">{card.title}</p>
              <p className="body-md">{card.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SPECIALISMS */}
      <section id="specialisms" className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24">
        <h2 className="h2 text-center mb-8 sm:mb-10">Our Specialisms</h2>
        <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
          {specialisms.map((specialism, index) => (
            <Link
              key={index}
              href={specialism.href}
              scroll={true}
              className="card-glass relative overflow-hidden hover:border-standard-hover group block"
            >
              <div className="flex items-center gap-4 mb-4 relative z-10">
                <IconBox icon={specialism.icon} index={index} variant="badge" size="md" />
                <h3 className="h3">{specialism.title}</h3>
              </div>
              <p className="body-md relative z-10">{specialism.description}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section id="industries" className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24 text-center">
        <h2 className="h2 mb-4 sm:mb-6">Industries We Serve</h2>
        <p className="body-md mb-8 sm:mb-12 max-w-5xl mx-auto">
          We operate across regulated and high-impact sectors where clarity, precision, and trust
          are essential.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {industries.map((industry, index) => (
            <Link
              key={index}
              href={industry.href}
              scroll
              className="relative border backdrop-blur-sm border-standard py-8 sm:py-10 px-3 sm:px-4 hover:border-standard-hover group block"
            >
              <div className="absolute -top-4 sm:-top-6 right-2 sm:right-4">
                <IconBox icon={industry.icon} index={index} variant="badge" size="md" />
              </div>
              <h3 className="text-small text-center">{industry.name}</h3>
            </Link>
          ))}
        </div>
      </section>

      {/* IMPACT */}
      <section id="impact" className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24">
        <h2 className="h2 text-center mb-4 sm:mb-6">Impact</h2>
        <p className="body-lg text-center mb-8 sm:mb-12 max-w-3xl mx-auto">
          Each engagement is measured not by hours delivered, but by impact sustained.
        </p>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 items-stretch">
          {impacts.map((impact, index) => {
            const borderClass =
              index % 2 === 0 ? "border-accentcolourteal" : "border-accentcolourorange";

            return (
              <Link
                key={index}
                href={impact.href}
                scroll
                className={`block border-l-4 border backdrop-blur-sm p-6 sm:p-8 ${borderClass} rounded-xl hover:border-standard-hover transition-all duration-200`}
              >
                <h3 className="h3 mb-2">{impact.client}</h3>
                <p className="body-md">{impact.description}</p>
              </Link>
            );
          })}
        </div>
      </section>

      {/* OUR CREDIBILITY */}
      <section className="max-w-6xl mx-auto px-6 mt-16 sm:mt-24 mb-16 sm:mb-24 grid md:grid-cols-2 gap-8 sm:gap-10 items-center">
        <div className="card-glass">
          <h2 className="h2 mb-4 flex items-center">Our Credibility</h2>
          <p className="body-md mb-4">
            Luminar's strength lies in its people strategists, engineers, and data specialists with
            proven experience delivering transformation in complex environments.
          </p>
          <p className="body-md">
            Our team combines strategic clarity with technical depth across AWS, Databricks,
            Snowflake, and Microsoft platforms.
          </p>
        </div>
        <div className="hidden md:flex justify-center">
          <Shield className="w-32 h-32 sm:w-48 sm:h-48 text-accentcolourteal" />
        </div>
      </section>
    </main>
  );
}